/**
 * Admin Service
 *
 * API endpoints for admin/lookup table operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type {
  Company,
  Portco,
  Segment,
  MeasureType,
  SponsorType,
  ScoreCategory,
  CostCategory,
  Score,
} from '@medsuite/models';

/**
 * Companies Service
 */
class CompaniesService {
  async getAll(): Promise<ApiResponse<Company[]>> {
    return apiClient.get<Company[]>('/admin/companies');
  }

  async getById(id: number): Promise<ApiResponse<Company>> {
    return apiClient.get<Company>(`/admin/companies/${id}`);
  }

  async create(data: { company_name: string }): Promise<ApiResponse<Company>> {
    return apiClient.post<Company>('/admin/companies', data);
  }

  async update(id: number, data: { company_name: string }): Promise<ApiResponse<Company>> {
    return apiClient.put<Company>(`/admin/companies/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/companies/${id}`);
  }
}

/**
 * Portfolio Companies Service
 */
class PortcosService {
  async getAll(params?: { companyId?: number }): Promise<ApiResponse<Portco[]>> {
    return apiClient.get<Portco[]>('/admin/portcos', { params });
  }

  async getById(id: number): Promise<ApiResponse<Portco>> {
    return apiClient.get<Portco>(`/admin/portcos/${id}`);
  }

  async create(data: { portco_name: string; company_id: number }): Promise<ApiResponse<Portco>> {
    return apiClient.post<Portco>('/admin/portcos', data);
  }

  async update(
    id: number,
    data: Partial<{ portco_name: string; company_id: number }>
  ): Promise<ApiResponse<Portco>> {
    return apiClient.put<Portco>(`/admin/portcos/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/portcos/${id}`);
  }
}

/**
 * Segments Service
 */
class SegmentsService {
  async getAll(): Promise<ApiResponse<Segment[]>> {
    return apiClient.get<Segment[]>('/admin/segments');
  }

  async getById(id: number): Promise<ApiResponse<Segment>> {
    return apiClient.get<Segment>(`/admin/segments/${id}`);
  }

  async create(data: {
    segment_name: string;
    program_name: string;
  }): Promise<ApiResponse<Segment>> {
    return apiClient.post<Segment>('/admin/segments', data);
  }

  async update(
    id: number,
    data: Partial<{ segment_name: string; program_name: string }>
  ): Promise<ApiResponse<Segment>> {
    return apiClient.put<Segment>(`/admin/segments/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/segments/${id}`);
  }
}

/**
 * Measure Types Service
 */
class MeasureTypesService {
  async getAll(params?: { costOrBenefit?: 'C' | 'B' }): Promise<ApiResponse<MeasureType[]>> {
    return apiClient.get<MeasureType[]>('/admin/measure-types', { params });
  }

  async getById(id: number): Promise<ApiResponse<MeasureType>> {
    return apiClient.get<MeasureType>(`/admin/measure-types/${id}`);
  }

  async create(data: {
    measure_type_name: string;
    cost_or_benefit: 'C' | 'B';
  }): Promise<ApiResponse<MeasureType>> {
    return apiClient.post<MeasureType>('/admin/measure-types', data);
  }

  async update(
    id: number,
    data: Partial<{ measure_type_name: string; cost_or_benefit: 'C' | 'B' }>
  ): Promise<ApiResponse<MeasureType>> {
    return apiClient.put<MeasureType>(`/admin/measure-types/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/measure-types/${id}`);
  }
}

/**
 * Sponsor Types Service
 */
class SponsorTypesService {
  async getAll(): Promise<ApiResponse<SponsorType[]>> {
    return apiClient.get<SponsorType[]>('/admin/sponsor-types');
  }

  async getById(id: number): Promise<ApiResponse<SponsorType>> {
    return apiClient.get<SponsorType>(`/admin/sponsor-types/${id}`);
  }

  async create(data: {
    sponsor_type_name: string;
    is_required: boolean;
    sponsor_type_sequence?: number;
  }): Promise<ApiResponse<SponsorType>> {
    return apiClient.post<SponsorType>('/admin/sponsor-types', data);
  }

  async update(
    id: number,
    data: Partial<{
      sponsor_type_name: string;
      is_required: boolean;
      sponsor_type_sequence: number;
    }>
  ): Promise<ApiResponse<SponsorType>> {
    return apiClient.put<SponsorType>(`/admin/sponsor-types/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/sponsor-types/${id}`);
  }

  async reorder(sponsorTypeIds: number[]): Promise<ApiResponse<SponsorType[]>> {
    return apiClient.patch<SponsorType[]>('/admin/sponsor-types/reorder', {
      sponsor_type_ids: sponsorTypeIds,
    });
  }
}

/**
 * Score Categories Service
 */
class ScoreCategoriesService {
  async getAll(): Promise<ApiResponse<ScoreCategory[]>> {
    return apiClient.get<ScoreCategory[]>('/admin/score-categories');
  }

  async getById(id: number): Promise<ApiResponse<ScoreCategory>> {
    return apiClient.get<ScoreCategory>(`/admin/score-categories/${id}`);
  }

  async create(data: {
    score_category_name: string;
    selection_type?: 'S' | 'M';
  }): Promise<ApiResponse<ScoreCategory>> {
    return apiClient.post<ScoreCategory>('/admin/score-categories', data);
  }

  async update(
    id: number,
    data: Partial<{
      score_category_name: string;
      selection_type: 'S' | 'M';
    }>
  ): Promise<ApiResponse<ScoreCategory>> {
    return apiClient.put<ScoreCategory>(`/admin/score-categories/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/score-categories/${id}`);
  }
}

/**
 * Cost Categories Service
 */
class CostCategoriesService {
  async getAll(): Promise<ApiResponse<CostCategory[]>> {
    return apiClient.get<CostCategory[]>('/admin/cost-categories');
  }

  async getById(id: number): Promise<ApiResponse<CostCategory>> {
    return apiClient.get<CostCategory>(`/admin/cost-categories/${id}`);
  }

  async create(data: { cost_category_name: string }): Promise<ApiResponse<CostCategory>> {
    return apiClient.post<CostCategory>('/admin/cost-categories', data);
  }

  async update(
    id: number,
    data: { cost_category_name: string }
  ): Promise<ApiResponse<CostCategory>> {
    return apiClient.put<CostCategory>(`/admin/cost-categories/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/cost-categories/${id}`);
  }
}

/**
 * Scores Service (admin lookup table for score templates)
 */
class ScoresService {
  async getAll(params?: { categoryId?: number }): Promise<ApiResponse<Score[]>> {
    return apiClient.get<Score[]>('/admin/scores', { params });
  }

  async getById(id: number): Promise<ApiResponse<Score>> {
    return apiClient.get<Score>(`/admin/scores/${id}`);
  }

  async getGrouped(): Promise<ApiResponse<{ [categoryId: number]: Score[] }>> {
    return apiClient.get<{ [categoryId: number]: Score[] }>('/admin/scores/grouped');
  }

  async create(data: {
    score_category_id: number;
    score_name: string;
    security_score: number;
    growth_score: number;
    efficiency_score: number;
    infrastructure_score: number;
    clinical_score: number;
    strategy_allocation: number;
    display_sequence?: number;
  }): Promise<ApiResponse<Score>> {
    return apiClient.post<Score>('/admin/scores', data);
  }

  async update(
    id: number,
    data: Partial<{
      score_category_id: number;
      score_name: string;
      security_score: number;
      growth_score: number;
      efficiency_score: number;
      infrastructure_score: number;
      clinical_score: number;
      strategy_allocation: number;
      display_sequence: number;
    }>
  ): Promise<ApiResponse<Score>> {
    return apiClient.put<Score>(`/admin/scores/${id}`, data);
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/scores/${id}`);
  }

  async reorder(id: number, direction: 'up' | 'down'): Promise<ApiResponse<{ scores: Score[] }>> {
    return apiClient.post<{ scores: Score[] }>(`/admin/scores/${id}/reorder`, { direction });
  }
}

/**
 * Admin Service - aggregates all admin sub-services
 */
class AdminService {
  public companies = new CompaniesService();
  public portcos = new PortcosService();
  public segments = new SegmentsService();
  public measureTypes = new MeasureTypesService();
  public sponsorTypes = new SponsorTypesService();
  public scoreCategories = new ScoreCategoriesService();
  public costCategories = new CostCategoriesService();
  public scores = new ScoresService();
}

export const adminService = new AdminService();
